/**
 * Route guard for the project result pages that — unlike
 * `MsalAuthenticationTemplate` — also admits Firebase *anonymous* (guest)
 * users. Guests reach a project via the home-page "try before you sign up"
 * upload flow and need to view (read-only) the result they just generated,
 * but they have `emailVerified === false`, so the standard template would
 * bounce them to `/sign-up`.
 *
 * Rules:
 * - Signed out -> redirect to `/sign-in`.
 * - Signed in, anonymous (guest) -> allowed (read-only result view).
 * - Signed in, real account, email NOT verified -> redirect to `/sign-up`.
 * - Signed in, real account, email verified -> allowed.
 *
 * Per-project access is still enforced server-side by the `userId` filter on
 * the project endpoints, so a guest can only ever load their own project.
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from './FirebaseAuthContext';

export function GuestProjectGuard({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useFirebaseAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      navigate('/sign-in', { replace: true });
    } else if (!user.isAnonymous && !user.emailVerified) {
      navigate('/sign-up', { replace: true });
    }
  }, [isLoaded, user, navigate]);

  if (!isLoaded) {
    return (
      <div
        className={`
          flex min-h-[40vh] items-center justify-center font-[Outfit]
          text-gray-500
        `}
      >
        Loading…
      </div>
    );
  }

  const allowed = !!user && (user.isAnonymous || user.emailVerified);
  if (!allowed) return null;
  return <>{children}</>;
}
